/**
 * VideoButton: 비디오 토글 + 카메라 선택 드롭다운 + 가상배경.
 *
 * v1 원본: RenewalComponents/Buttons/VideoButton/VideoButtonPresenter.js
 * v1 CSS: .rtc_new3 .newHeader_btn > .newHeader_subBtn + .newHeader_subBtn--dropdown
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';

interface VideoButtonProps {
  onToggleVideo: () => void;
  onOpenSettings: () => void;
  onOpenSettingsBackground?: () => void;
  onOpenSettingsVideo?: () => void;
  onChangeDevice?: (type: string, deviceId: string) => void;
}

const VideoButton: React.FC<VideoButtonProps> = ({ onToggleVideo, onOpenSettings, onOpenSettingsBackground, onOpenSettingsVideo, onChangeDevice }) => {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);
  const isMuted = local.videoMuted;
  const videoInputDevices = local.videoInputDevices;
  const videoInputDeviceId = local.videoInputDeviceId;

  const [isListOpen, setIsListOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 닫기
  useEffect(() => {
    if (!isListOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsListOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isListOpen]);

  // 키보드 단축키: Ctrl+Shift+C
  const handleToggle = useCallback(() => {
    onToggleVideo();
  }, [onToggleVideo]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'C') {
        handleToggle();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleToggle]);

  const handleSelectDevice = (deviceId: string) => {
    onChangeDevice?.('videoinput', deviceId);
    setIsListOpen(false);
  };

  const handleVirtualBackground = () => {
    if (onOpenSettingsBackground) {
      onOpenSettingsBackground();
    } else {
      // Fallback for cases where VideoButton is not inside Footer
      dispatch(uiActions.setVirtualBackgroundOpen(true));
    }
    setIsListOpen(false);
  };

  const handleVideoSettings = () => {
    if (onOpenSettingsVideo) {
      onOpenSettingsVideo();
    } else {
      onOpenSettings(); // Fallback to general settings
    }
    setIsListOpen(false);
  };

  return (
    <div ref={containerRef} className={`newHeader_btnItem${isListOpen ? ' open' : ''}`}>
      <div className="newHeader_btn">
        <button
          type="button"
          className={`newHeader_subBtn${isMuted ? ' disabled' : ''}`}
          onClick={handleToggle}
        >
          <span className="newHeader_btnIcon icoCam" />
        </button>
        <button
          type="button"
          className="newHeader_subBtn newHeader_subBtn--dropdown"
          onClick={() => setIsListOpen(!isListOpen)}
        >
          <p className="newHeader_btnText">카메라</p>
        </button>
      </div>
      {isListOpen && (
        <div className="viewerHeader__layer">
          <strong className="viewerHeader__layerTitle">카메라 선택</strong>
          <ul className="viewerHeader__layerList" style={{overflowX:"hidden"}}>
            {videoInputDevices.map((device) => (
              <li key={device.deviceId}>
                <button
                  type="button"
                  className={`viewerHeader__layerListBtn${device.deviceId === videoInputDeviceId ? ' selected' : ''}`}
                  onClick={() => handleSelectDevice(device.deviceId)}
                >
                  {device.label || `카메라 ${device.deviceId.slice(0, 5)}`}
                </button>
              </li>
            ))}
            <li className='bar'>
              <button
                type="button"
                className="viewerHeader__layerListBtn"
                onClick={handleVideoSettings}
              >
                영상 설정
              </button>
            </li>
            <li>
              <button
                type="button"
                className="viewerHeader__layerListBtn"
                onClick={handleVirtualBackground}
              >
                가상배경 설정
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default VideoButton;
